import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { ExperiencePerformanceTypes, ExperienceTypes, visibilityChangeObserver } from '@atlassian/ufo-experimental';
import { getGlobalEventStream, setGlobalEventStream } from '@atlassian/ufo-experimental/global-stream-buffer';
import { ufolog, ufowarn } from '@atlassian/ufo-experimental/logger';
import { PageVisibleState, UFOGlobalEventStreamEventType } from '@atlassian/ufo-experimental/types';
import { UFO_BUILD_VERSION } from '../../buildVersion';
import { TenantType } from '../../types';
import { UFOGlobalEventStream } from '../event-stream/event-stream';
import { getPerformancePayload } from './performance';
import { pageVisibleState } from './performance/plugins/page-visible-state';
import { resourceTimingBuffer } from './performance/resource-timings-buffer';
import { appFramework } from './plugins/app/framework';
import { appVersion } from './plugins/app/version';
import { eventBrowserPlugin } from './plugins/browser';
import { eventCpusPlugin } from './plugins/cpus';
import { featureFlags } from './plugins/feature-flags';
import { eventMemoryPlugin } from './plugins/memory';
import { eventNetworkPlugin } from './plugins/network';
import { ssrFeatureFlags } from './plugins/ssr/feature-flags';
import { ssrSuccess } from './plugins/ssr/success';
import { Route } from './route';
import { isInitialPageLoad } from './utils/is-initial-page-load';
import { getPerformanceObject, getWindowObject } from './utils/window-helper';
const UNKNOWN_VALUE = 'unknown';
const DEFAULT_PAYLOAD_DATA = {
  schemaVersion: UNKNOWN_VALUE,
  id: UNKNOWN_VALUE,
  uuid: UNKNOWN_VALUE,
  state: {
    id: UNKNOWN_VALUE
  },
  result: {
    startTime: 0,
    duration: 0,
    success: false
  },
  type: ExperienceTypes.Experience,
  category: UNKNOWN_VALUE,
  performanceType: ExperiencePerformanceTypes.Custom,
  metadata: {},
  explicitTimings: {},
  metrics: {
    startTime: 0,
    endTime: 0,
    marks: []
  },
  platform: null,
  children: [],
  pageVisibleState: PageVisibleState.MIXED,
  isSSROutputAsFMP: false,
  timings: [],
  featureFlags: []
};
class Publisher {
  constructor() {
    var _getWindowObject, _getWindowObject$loca;
    _defineProperty(this, "gasv3", null);
    _defineProperty(this, "product", '');
    _defineProperty(this, "region", UNKNOWN_VALUE);
    _defineProperty(this, "hostname", ((_getWindowObject = getWindowObject()) === null || _getWindowObject === void 0 ? void 0 : (_getWindowObject$loca = _getWindowObject.location) === null || _getWindowObject$loca === void 0 ? void 0 : _getWindowObject$loca.hostname) || '');
    _defineProperty(this, "tenantType", TenantType.RealUser);
    _defineProperty(this, "app", {
      version: {
        web: UNKNOWN_VALUE
      }
    });
    _defineProperty(this, "performanceConfig", {});
    _defineProperty(this, "buffer", []);
    _defineProperty(this, "featureFlagClient", null);
    _defineProperty(this, "featureFlags", []);
    _defineProperty(this, "ssr", null);
    _defineProperty(this, "resourceTimings", null);
    _defineProperty(this, "bundleEvalTimings", null);
    _defineProperty(this, "route", Route);
    _defineProperty(this, "plugins", null);
    _defineProperty(this, "allowlist", () => true);
    _defineProperty(this, "getPluginsMetrics", data => {
      const result = {};
      if (this.plugins) {
        this.plugins.forEach(plugin => {
          const pluginOutput = plugin({
            ...data
          });
          if (pluginOutput === null || typeof pluginOutput !== 'object') {
            return;
          }
          Object.entries(pluginOutput).forEach(([key, value]) => {
            result[`${this.product}:${key}`] = value;
          });
        });
      }
      return result;
    });
  }
  publish(data) {
    var _getPerformanceObject, _getPerformanceObject2;
    const payloadStart = ((_getPerformanceObject = getPerformanceObject()) === null || _getPerformanceObject === void 0 ? void 0 : _getPerformanceObject.now()) || 0;
    const experienceKey = this.getExperienceKey(data);
    if (!this.allowlist(experienceKey, data)) {
      ufowarn(`experience ${experienceKey} does not meet requirements of allowlist`);
      return;
    }

    // it is used to setup default properties on the data to prevent errors
    const supportedData = {
      ...DEFAULT_PAYLOAD_DATA,
      ...data
    };
    const payload = {
      actionSubject: 'experience',
      action: 'measured',
      source: 'measured',
      tags: ['observability'],
      attributes: {
        properties: {
          'event:source': {
            name: 'ufo/web',
            version: supportedData.schemaVersion
          },
          'event:schema': UFO_BUILD_VERSION || UNKNOWN_VALUE,
          'event:product': this.product,
          'event:region': this.region,
          'event:hostname': this.hostname,
          'event:route': this.route.getRoute(),
          ...eventBrowserPlugin(),
          ...eventCpusPlugin(),
          ...eventMemoryPlugin(),
          ...eventNetworkPlugin(),
          ...appFramework(this.app),
          ...appVersion(this.app),
          'experience:name': supportedData.id,
          // platform.fe.page-load|interaction|custom|page-segment.editor
          'experience:key': experienceKey,
          'experience:status': supportedData.state.id,
          'experience:duration': supportedData.result.duration,
          ...(supportedData.result.startTime !== null ? {
            'experience:startTime': supportedData.result.startTime
          } : null),
          ...(supportedData.uuid ? {
            'experience:spanId': supportedData.uuid
          } : null),
          ...(this.tenantType !== TenantType.RealUser ? {
            'event:tenantType': this.tenantType
          } : null),
          ...this.mapMetadataToPayload(supportedData.metadata),
          ...this.extractPageLoadData(supportedData),
          ...pageVisibleState(supportedData),
          ...(isInitialPageLoad(supportedData) && ssrSuccess(this.ssr)),
          ...(isInitialPageLoad(supportedData) && ssrFeatureFlags(this.ssr)),
          ...(this.featureFlagClient && featureFlags(this.featureFlagClient, this.featureFlags, supportedData))
        },
        performance: this.getPerformanceMetrics(supportedData),
        task: supportedData.id
      }
    };
    const payloadEnd = ((_getPerformanceObject2 = getPerformanceObject()) === null || _getPerformanceObject2 === void 0 ? void 0 : _getPerformanceObject2.now()) || 0;
    payload.attributes.performance.payloadTime = payloadEnd - payloadStart;
    if (this.gasv3) {
      this.gasv3.sendOperationalEvent(payload);
    } else {
      this.buffer.push(payload);
    }
    ufolog('PAYLOAD:', JSON.stringify(payload, null, 2));
  }
  setGlobalEventStream() {
    if (!(getGlobalEventStream() instanceof UFOGlobalEventStream)) {
      const eventStream = new UFOGlobalEventStream();
      eventStream.push({
        type: UFOGlobalEventStreamEventType.SUBSCRIBE,
        payload: {
          experienceId: '__SUBSCRIBE_ALL__',
          callback: this.publish.bind(this)
        }
      });
      setGlobalEventStream(eventStream);
    }
  }
  setRoute(newRoute) {
    this.route.setRoute(newRoute);
  }
  setup(config) {
    visibilityChangeObserver.start();
    resourceTimingBuffer.start();
    if (!this.product) {
      this.product = config.product || '';
    }
    if (config.allowlist) {
      this.allowlist = config.allowlist;
    }
    if (config.tenantType) {
      this.tenantType = config.tenantType;
    }
    if (config.hostname) {
      this.hostname = config.hostname;
    }
    if (config.region) {
      this.region = config.region;
    }
    if (config.app) {
      this.app = config.app;
    }
    if (config.featureFlagClient) {
      this.featureFlagClient = config.featureFlagClient;
    }
    if (config.featureFlags) {
      this.featureFlags = config.featureFlags;
    }
    if (config.ssr) {
      this.ssr = config.ssr;
    }
    if (config.bundleEvalTimings) {
      this.bundleEvalTimings = config.bundleEvalTimings;
    }
    if (config.resourceTimings) {
      this.resourceTimings = config.resourceTimings;
    }
    if (config.performanceConfig) {
      this.performanceConfig = config.performanceConfig;
    }
    if (config.plugins) {
      this.plugins = config.plugins;
    }
    this.setGlobalEventStream();
    if (!this.gasv3) {
      this.gasv3 = config.gasv3;
      this.buffer.forEach(this.gasv3.sendOperationalEvent);
    }
  }
  mapMetadataToPayload(metadata) {
    const payload = {};
    Object.entries(metadata).forEach(([key, value]) => {
      if (value !== undefined) {
        payload[`custom:${key}`] = value;
      }
    });
    return payload;
  }
  extractPageLoadData(data) {
    if ('initial' in data) {
      return {
        'experience:pageLoadType': data.initial ? 'initial' : 'transition'
      };
    }
    return {};
  }
  getPrefix(data) {
    if (data.platform !== null) {
      return 'platform';
    }
    return this.product;
  }
  getExperienceKey(data) {
    const keyParts = [this.getPrefix(data), 'fe', data.type];
    if (data.platform) {
      keyParts.push(data.platform.component);
    }
    keyParts.push(data.id);
    return keyParts.join('.');
  }
  getExperiencePerformanceKey(data) {
    const keyParts = [this.getPrefix(data), 'fe', data.performanceType];
    if (data.platform) {
      keyParts.push(data.platform.component);
    }
    keyParts.push(data.id);
    return keyParts.join('.');
  }
  getExperiencePerformanceType(data) {
    return data.performanceType;
  }
  getPerformanceMetrics(data) {
    return {
      'event:key': this.getExperiencePerformanceKey(data),
      'event:type': this.getExperiencePerformanceType(data),
      ...getPerformancePayload(data, this.ssr, this.bundleEvalTimings, this.resourceTimings, this.performanceConfig),
      ...this.getPluginsMetrics(data)
    };
  }
}
export const payloadPublisher = new Publisher();